import type { ContentPage } from "@/lib/content/types";

// CRM › CRM Analytics hub.
export const crmAnalytics: ContentPage = {
  slug: "crm-analytics",
  template: "hrmsFeature",
  seo: {
    title: "CRM Analytics Software - Reports, Dashboards & KPIs | GetBiz CRM",
    description: "Measure sales performance with GetBiz CRM analytics — build reports, charts, cohorts, KPI dashboards and funnels to turn data into decisions.",
    keywords: ["crm analytics", "sales analytics software", "crm reports", "crm dashboard", "sales kpi", "sales funnel analysis", "GetBiz CRM"],
    ogImage: "/assets/data/seoimages/crm-analytics.webp",
  },
  leaf: {
    hub: true,
    benefits: [
      { t: "Reports in minutes", d: "Build reports without waiting on IT." },
      { t: "See it visually", d: "Charts make trends and outliers obvious." },
      { t: "Live dashboards", d: "Pipeline, activity and revenue at a glance." },
      { t: "Track KPIs", d: "Monitor the metrics that matter in real time." },
      { t: "Find the leaks", d: "Funnels show exactly where deals stall." },
      { t: "Understand segments", d: "Cohorts reveal behavior over time." },
      { t: "Data-driven decisions", d: "Act on facts, not gut feel." },
      { t: "Always current", d: "Every insight updates in real time." },
    ],
    process: {
      title: "How CRM Analytics Works, Step by Step",
      description: "Turn raw sales activity into clear, real-time insight you can act on.",
      steps: [
        { title: "Collect Data", caption: "Every lead, deal and activity flows into the CRM.", image: "/assets/images/img/product/crm/collect-data.webp" },
        { title: "Build Reports", caption: "Filter and schedule reports on any object.", image: "/assets/images/img/product/crm/build-reports.webp" },
        { title: "Visualize", caption: "Turn reports into charts and dashboards.", image: "/assets/images/img/product/crm/visualize.webp" },
        { title: "Track KPIs", caption: "Monitor targets against real-time metrics.", image: "/assets/images/img/product/crm/track-kpis.webp" },
        { title: "Find & Fix", caption: "Funnels reveal where to improve.", image: "/assets/images/img/product/crm/find-fix.webp" },
      ],
    },
    highlights: {
      title: "Key Highlights of CRM Analytics",
      items: [
        { image: "/assets/images/img/product/crm/build-reports-fats.webp", title: "Build any report fast.", href: "/crm-software/crm-analytics/reports/", description: "Create, filter and schedule reports on leads, deals, activity and revenue — no IT required.", checklist: ["Any object","Filters","Scheduled","No IT"] },
        { image: "/assets/images/img/product/crm/trends-visualy.webp", title: "See trends visually.", href: "/crm-software/crm-analytics/charts/", description: "Turn any report into clear bar, line, pie or funnel charts so trends and outliers jump out instantly.", checklist: ["Bar/line/pie","Funnel","Instant","Shareable"] },
        { image: "/assets/images/img/product/crm/deal-stall.webp", title: "Find where deals stall.", href: "/crm-software/crm-analytics/funnels/", description: "Visualize your pipeline as a funnel to pinpoint the stages where deals leak, so you know exactly where to improve.", checklist: ["Stage view","Find leaks","Conversion","Improve"] },
      ],
    },
    hero: {
      caption: "CRM Analytics",
      title: "Turn Your Sales Data Into Decisions",
      description: "Reports, charts, cohorts, KPIs and funnels — GetBiz CRM turns raw activity into clear, real-time insight so you always know what is working and what to fix.",
      h1: "caption",
    },
    split: {
      title: "Everything You Need for CRM Analytics",
      bullets: [
        { title: "Real-Time Reports", description: "Build and schedule reports on any object without waiting on IT." },
        { title: "Visual Dashboards", description: "See pipeline, activity and revenue at a glance in live dashboards." },
        { title: "Cohort & Funnel Insight", description: "Understand where deals convert and where they leak." },
        { title: "KPI Tracking", description: "Track the metrics that matter against targets, in real time." },
      ],
      image: "/assets/images/img/product/crm/crm-analytics.webp",
      imageAlt: "CRM Analytics — GetBiz CRM",
    },
    cardsHeading: {
      title: "Key Features of CRM Analytics",
      description: "Explore everything CRM Analytics brings to your GetBiz CRM.",
    },
    cards: [
      { title: "Reports", href: "/crm-software/crm-analytics/reports/", description: "Create, filter and schedule reports on leads, deals, activity and revenue.", image: "/assets/images/img/product/crm/reports.webp", imageAlt: "GetBiz CRM reports", checklist: ["Any Object","Filters","Scheduled","No IT"] },
      { title: "Charts", href: "/crm-software/crm-analytics/charts/", description: "Turn any report into clear bar, line, pie or funnel charts so trends and outliers jump out instantly.", image: "/assets/images/img/product/crm/charts.webp", imageAlt: "GetBiz CRM charts", checklist: ["Bar/Line/Pie","Funnel","Instant","Shareable"] },
      { title: "Cohorts", href: "/crm-software/crm-analytics/cohorts/", description: "Group leads and customers into cohorts to see how conversion, retention and value change across segments and time.", image: "/assets/images/img/product/crm/cohorts.webp", imageAlt: "GetBiz CRM cohorts", checklist: ["Segments","Retention","Over Time","Compare"] },
      { title: "KPIs", href: "/crm-software/crm-analytics/kpis/", description: "Set targets and monitor sales KPIs.", image: "/assets/images/img/product/crm/kpis.webp", imageAlt: "GetBiz CRM kpis", checklist: ["Targets","Real-Time","Dashboards","Alerts"] },
      { title: "Funnels", href: "/crm-software/crm-analytics/funnels/", description: "Visualize your pipeline as a funnel to find the stages where deals stall, so you know precisely where to improve.", image: "/assets/images/img/product/crm/Funnels.webp", imageAlt: "GetBiz CRM funnels", checklist: ["Stage View","Find Leaks","Conversion","Improve"] },
    ],
    testimonials: {
      title: "Businesses across India love & trust GetBiz!",
      description: "See why teams choose GetBiz CRM to run their sales.",
    },
    bookDemoTitle: "Ready to get more from crm analytics?",
    relatedBlogs: "static",
    metaKey: "crm-analytics",
  },
  sections: [],
};
